import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Store, Plus, Edit, Trash2, Save, X, Package, Phone, Mail } from 'lucide-react';
import { getSuppliers, addSupplier, updateSupplier, deleteSupplier, getUserStructures } from '../../api';

const SupplierManagement = () => {
  const [structures, setStructures] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    structures: []
  });

  useEffect(() => {
    fetchStructuresAndSuppliers();
  }, []);

  const fetchStructuresAndSuppliers = async () => {
    try {
      setLoading(true);
      const structuresResponse = await getUserStructures();
      setStructures(structuresResponse.data);

      if (structuresResponse.data.length === 0) {
        setError('Aucune structure assignée à votre compte');
        setLoading(false);
        return;
      }

      // Fetch suppliers from all structures the user has access to
      const allSuppliers = new Map(); // Use Map to avoid duplicates

      for (const structure of structuresResponse.data) {
        try {
          const suppliersResponse = await getSuppliers(structure.id);
          suppliersResponse.data.forEach(supplier => {
            // Use supplier.id as key to avoid duplicates across structures
            if (!allSuppliers.has(supplier.id)) {
              allSuppliers.set(supplier.id, supplier);
            }
          });
        } catch (err) {
          console.error(`Error fetching suppliers for structure ${structure.name}:`, err);
        }
      }

      setSuppliers(Array.from(allSuppliers.values()));
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des données');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.structures.length === 0) {
      setError('Veuillez sélectionner au moins une structure');
      return;
    }

    try {
      const supplierData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        structures: formData.structures
      };

      if (editingSupplier) {
        await updateSupplier(editingSupplier.id, supplierData);
      } else {
        await addSupplier(supplierData);
      }
      await fetchStructuresAndSuppliers();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (supplierId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir archiver ce fournisseur ?')) {
      return;
    }
    try {
      // Delete supplier (no need to specify structure anymore with many-to-many)
      await deleteSupplier(supplierId, structures[0]?.id);
      await fetchStructuresAndSuppliers();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      email: supplier.email || '',
      phone: supplier.phone || '',
      address: supplier.address || '',
      structures: supplier.structures?.map(s => s.structureId) || []
    });
    setShowAddForm(true);
  };

  const toggleStructure = (structureId) => {
    setFormData(prev => {
      const isSelected = prev.structures.includes(structureId);
      return {
        ...prev,
        structures: isSelected
          ? prev.structures.filter(id => id !== structureId)
          : [...prev.structures, structureId]
      };
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      structures: []
    });
    setEditingSupplier(null);
    setShowAddForm(false);
  };

  if (loading) {
    return <div className="p-4 text-center">Chargement...</div>;
  }

  if (structures.length === 0) {
    return <div className="p-4 text-center">Aucune structure assignée à votre compte</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center mb-6">
        <h1 className="text-3xl font-bold">Gestion des fournisseurs</h1>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-lg">
          {error}
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store size={24} />
              {editingSupplier ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Nom du fournisseur *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                />
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold mb-3">Informations de contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      <Mail className="inline mr-1" size={16} />
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="contact@fournisseur.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      <Phone className="inline mr-1" size={16} />
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="01 23 45 67 89"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium mb-2">
                    Adresse
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    rows="2"
                    placeholder="Adresse complète..."
                  />
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold mb-3">Structures * {structures.length > 1 && `(${formData.structures.length} sélectionnée${formData.structures.length > 1 ? 's' : ''})`}</h3>
                <p className="text-sm text-gray-600 mb-3">
                  {structures.length > 1
                    ? 'Sélectionnez les structures auxquelles ce fournisseur sera accessible'
                    : 'Ce fournisseur sera accessible à votre structure'}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {structures.map(structure => (
                    <label
                      key={structure.id}
                      className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition"
                    >
                      <input
                        type="checkbox"
                        checked={formData.structures.includes(structure.id)}
                        onChange={() => toggleStructure(structure.id)}
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <span className="text-sm font-medium">{structure.name}</span>
                    </label>
                  ))}
                </div>
                {formData.structures.length === 0 && (
                  <p className="text-sm text-orange-600 mt-2">
                    ⚠️ Veuillez sélectionner au moins une structure
                  </p>
                )}
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
                >
                  <X size={20} />
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition"
                >
                  <Save size={20} />
                  {editingSupplier ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Suppliers List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {suppliers.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            <Store size={48} className="mx-auto mb-4 text-gray-400" />
            <p>Aucun fournisseur enregistré</p>
            <p className="text-sm">Cliquez sur "Nouveau fournisseur" pour commencer</p>
          </div>
        ) : (
          suppliers.map((supplier) => (
            <Card key={supplier.id} className={`${!supplier.active ? 'opacity-60 border-gray-300' : ''}`}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Store size={20} />
                    {supplier.name}
                    {!supplier.active && (
                      <span className="text-xs bg-gray-200 px-2 py-1 rounded">Archivé</span>
                    )}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEdit(supplier)}
                      className="p-2 hover:bg-gray-100 rounded transition"
                      title="Modifier"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(supplier.id)}
                      className="p-2 hover:bg-red-100 text-red-600 rounded transition"
                      title="Archiver"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {supplier.email && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Mail size={14} />
                      <a href={`mailto:${supplier.email}`} className="hover:underline">
                        {supplier.email}
                      </a>
                    </div>
                  )}

                  {supplier.phone && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Phone size={14} />
                      <a href={`tel:${supplier.phone}`} className="hover:underline">
                        {supplier.phone}
                      </a>
                    </div>
                  )}

                  {supplier.address && (
                    <div className="text-gray-600 text-xs mt-2 pt-2 border-t">
                      {supplier.address}
                    </div>
                  )}

                  {supplier.structures && supplier.structures.length > 1 && (
                    <div className="mt-3 pt-2 border-t">
                      <p className="text-xs text-gray-500 mb-1">Utilisé dans:</p>
                      <div className="flex flex-wrap gap-1">
                        {supplier.structures.map(s => (
                          <span
                            key={s.structureId}
                            className="px-2 py-1 bg-primary/5 text-primary-dark rounded text-xs"
                          >
                            {s.structure?.name || s.name || 'Structure'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t text-xs text-gray-500">
                  Créé le {new Date(supplier.createdAt).toLocaleDateString('fr-FR')}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      {!showAddForm && (
        <button
          onClick={() => {
            setFormData({ name: '', email: '', phone: '', address: '', structures: structures.length === 1 ? [structures[0].id] : [] });
            setShowAddForm(true);
          }}
          className="fixed bottom-6 right-6 z-50 bg-primary text-white rounded-full p-4 shadow-lg hover:bg-primary-dark transition-all hover:scale-105"
          title="Nouveau fournisseur"
        >
          <Plus size={24} />
        </button>
      )}
    </div>
  );
};

export default SupplierManagement;
